/**
 * User identity management functionality
 * Based on Adobe Launch implementation that tracks changes in sessionStorage
 */ import * as amplitude from '@amplitude/analytics-browser';
import { getCvidCookie, getUserUuid, isWindowDefined } from '@chegg/utils';
import { isHardLoggedIn } from './utils';
import { getSubscriptionStatusFromToken } from './subscription-utils';
/**
 * Get user data from various sources for identity management
 */ function getUserIdentityData() {
    // Get auth status based on authentication state
    const getAuthStatus = ()=>{
        return isHardLoggedIn() ? 'Hard Logged In' : 'Logged Out';
    };
    return {
        authStatus: getAuthStatus(),
        deviceId: getCvidCookie() || '',
        userId: getUserUuid() || '',
        cheggVisitorUuid: getCvidCookie() || '',
        cheggUuid: getUserUuid() || '',
        subscriptionStatus: getSubscriptionStatusFromToken()
    };
}
/**
 * Set user identity in Amplitude with change detection
 * Based on Adobe Launch implementation that tracks changes in sessionStorage
 */ export function setAmplitudeUserIdentity(forceUpdate = false) {
    if (!isWindowDefined || typeof sessionStorage === 'undefined') {
        console.warn('Cannot set Amplitude user identity: window or sessionStorage not available');
        return;
    }
    try {
        // Get current values from sessionStorage
        const storedAuthStatus = sessionStorage.getItem('auth_status');
        const storedDeviceId = sessionStorage.getItem('device_id');
        const storedUserId = sessionStorage.getItem('user_id');
        const storedSubscriptionStatus = sessionStorage.getItem('subscription_status');
        const storedCheggUuid = sessionStorage.getItem('chegg_uuid');
        const storedCheggVisitorUuid = sessionStorage.getItem('chegg_visitor_uuid');
        // Get new values
        const newData = getUserIdentityData();
        // Check if this is the first time setting identity (no stored values)
        const isFirstTimeSetup = !storedAuthStatus && !storedDeviceId && !storedUserId;
        const shouldSetAll = forceUpdate || isFirstTimeSetup;
        // Create identify event
        const identifyEvent = new amplitude.Identify();
        let shouldUpdate = false;
        // Helper function to update identity property
        const updateIdentityProperty = (config)=>{
            // Update if forceUpdate, first time setup, or newValue is different from stored value
            if (shouldSetAll || config.storedValue !== config.newValue) {
                // Handle session storage - convert null to string
                sessionStorage.setItem(config.sessionKey, String(config.newValue));
                // Handle Amplitude - all values including null converted to string
                identifyEvent.set(config.key, String(config.newValue));
                return true;
            }
            return false;
        };
        // Update all identity properties
        shouldUpdate = updateIdentityProperty({
            key: 'auth_status',
            newValue: newData.authStatus,
            storedValue: storedAuthStatus,
            sessionKey: 'auth_status'
        }) || shouldUpdate;
        shouldUpdate = updateIdentityProperty({
            key: 'device_id',
            newValue: newData.deviceId,
            storedValue: storedDeviceId,
            sessionKey: 'device_id'
        }) || shouldUpdate;
        shouldUpdate = updateIdentityProperty({
            key: 'user_id',
            newValue: newData.userId,
            storedValue: storedUserId,
            sessionKey: 'user_id'
        }) || shouldUpdate;
        shouldUpdate = updateIdentityProperty({
            key: 'chegg_visitor_uuid',
            newValue: newData.cheggVisitorUuid,
            storedValue: storedCheggVisitorUuid,
            sessionKey: 'chegg_visitor_uuid'
        }) || shouldUpdate;
        shouldUpdate = updateIdentityProperty({
            key: 'chegg_uuid',
            newValue: newData.cheggUuid,
            storedValue: storedCheggUuid,
            sessionKey: 'chegg_uuid'
        }) || shouldUpdate;
        // Handle subscription status - null when no token, object when token present
        const subscriptionStatusValue = newData.subscriptionStatus ? JSON.stringify(newData.subscriptionStatus) : 'null';
        shouldUpdate = updateIdentityProperty({
            key: 'subscription_status',
            newValue: subscriptionStatusValue,
            storedValue: storedSubscriptionStatus,
            sessionKey: 'subscription_status'
        }) || shouldUpdate;
        // Only send the identify event if we updated at least one value
        if (shouldUpdate) {
            amplitude.identify(identifyEvent);
        }
    } catch (error) {
        console.error('Error setting Amplitude user identity:', error);
    }
}
